import type { DurableLeaseLiveness, DurableOwnershipPort, DurableUploadTransaction } from "./ports";
import type { SmallFileUploadClaim } from "./smallFileLane";
import {
	DURABLE_UPLOAD_DATABASE_NAME,
	DURABLE_UPLOAD_DATABASE_VERSION,
	DURABLE_UPLOAD_STORE_NAMES,
	durableIdentityKey,
	poisonRecordKey,
	schedulerLeaseKey,
	writerLeaseKey,
	type DurableItemIdentity,
	type DurableLeaseRecord,
	type DurableLeaseToken,
	type DurablePoisonRecord,
	type DurableUploadStoreName,
	type DurableWorkRecord
} from "./durableUploadSchema";

export interface DurableUploadDatabase {
	transaction<T>(
		storeNames: readonly DurableUploadStoreName[],
		mode: "readonly" | "readwrite",
		callback: (transaction: DurableUploadTransaction) => Promise<T> | T
	): Promise<T>;
}

export interface IndexedDbDurableOwnershipOptions {
	databaseName?: string;
	databaseVersion?: number;
	/** App stores are created if absent and are never deleted or migrated here. */
	appOwnedStoreNames?: readonly string[];
	/** App stores that need an explicit IndexedDB key path when first created. */
	appOwnedStoreDefinitions?: readonly {
		name: string;
		keyPath?: string | string[];
		autoIncrement?: boolean;
	}[];
	indexedDb?: IDBFactory;
}

export class DurableOwnershipUnavailableError extends Error {
	readonly code = "DURABLE_OWNERSHIP_UNAVAILABLE";

	constructor(message = "Durable upload ownership is unavailable") {
		super(message);
		this.name = "DurableOwnershipUnavailableError";
	}
}

export class DurableOwnershipAnomalyError extends Error {
	readonly code = "DURABLE_OWNERSHIP_ANOMALY";

	constructor(message: string) {
		super(message);
		this.name = "DurableOwnershipAnomalyError";
	}
}

/**
 * Called inside each transaction callback, never before it (LSP-22804): a transaction can wait behind a paused or
 * blocked database, and a `now` read before that wait would be compared with records other tabs wrote meanwhile.
 */
function validNow(now: number | undefined): number {
	const value = now ?? Date.now();
	if (!Number.isSafeInteger(value)) throw new DurableOwnershipAnomalyError("Durable ownership clock is invalid");
	return value;
}

function validTtl(ttlMs: number): number {
	if (!Number.isFinite(ttlMs) || ttlMs <= 0) throw new Error("Durable upload lease TTL must be positive and finite");
	return ttlMs;
}

function leaseExpiry(now: number, ttlMs: number): number {
	const expiresAt = now + validTtl(ttlMs);
	if (!Number.isSafeInteger(expiresAt) || expiresAt <= now) {
		throw new DurableOwnershipAnomalyError("Durable upload lease expiry is invalid");
	}
	return expiresAt;
}

/**
 * The expiry a release stamps.
 *
 * `expiresAt = now` looks terminal but is only terminal for a clock that never steps backwards: a
 * release at t=200 followed by a renewal at t=199 reads as still-live (`200 <= 199` is false). While
 * the fence compared `expiresAt` that was masked, because the released record no longer matched any
 * token. Once it stopped comparing it, the released token matched again and could renew a dead lease
 * back to life — blocking a successor's `acquireWriter` for a further TTL. `Date.now()` regresses in
 * practice via NTP correction, suspend/resume, and manual clock changes.
 *
 * `acquiredAt` is the minimum value `assertRecordSafe` accepts (it requires `expiresAt >=
 * acquiredAt`), and `assertClockSafe` refuses any `now` earlier than `acquiredAt` outright — so a
 * record released this way satisfies `expiresAt <= now` for every clock this module will accept.
 * Release becomes a property of the record instead of a race against the clock.
 *
 * Exported because the fence lives here but not every site that kills a lease does: contact-admin's
 * `handoffFileUploadSessionToCancellation` writes the lease record directly to hand a cancelled
 * session over. Any code that ends a lease by rewriting `expiresAt` must stamp this, not `now`.
 */
export function releasedExpiry(record: DurableLeaseRecord): number {
	return record.acquiredAt;
}

function assertOwnerId(ownerId: unknown): asserts ownerId is string {
	if (typeof ownerId !== "string" || ownerId.length === 0 || ownerId.trim() !== ownerId) {
		throw new DurableOwnershipAnomalyError("Durable upload lease owner id is invalid");
	}
}

function assertOriginKey(originKey: unknown): asserts originKey is string {
	if (typeof originKey !== "string" || originKey.length === 0 || originKey.trim() !== originKey) {
		throw new DurableOwnershipAnomalyError("Durable upload origin key is invalid");
	}
}

function assertIdentity(identity: DurableItemIdentity | undefined): void {
	if (identity) {
		try {
			durableIdentityKey(identity);
		} catch {
			throw new DurableOwnershipAnomalyError("Durable upload item identity is invalid");
		}
	}
}

function assertRecordSafe(record: DurableLeaseRecord): void {
	if (!record || typeof record !== "object") {
		throw new DurableOwnershipAnomalyError("Durable upload lease record is corrupt");
	}
	assertOwnerId(record.ownerId);
	assertOriginKey(record.originKey);
	if (record.kind !== "scheduler" && record.kind !== "writer") {
		throw new DurableOwnershipAnomalyError("Durable upload lease kind is corrupt");
	}
	if (record.itemId === undefined && record.takeId !== undefined) {
		throw new DurableOwnershipAnomalyError("Durable upload scheduler identity is corrupt");
	}
	if (record.kind === "writer" && typeof record.itemId !== "string") {
		throw new DurableOwnershipAnomalyError("Durable upload writer identity is corrupt");
	}
	assertIdentity(record.itemId === undefined ? undefined : { itemId: record.itemId, takeId: record.takeId });
	const expectedKey =
		record.kind === "scheduler"
			? schedulerLeaseKey(record.originKey)
			: writerLeaseKey(record.originKey, {
					itemId: record.itemId!,
					takeId: record.takeId
				});
	if (record.key !== expectedKey) {
		throw new DurableOwnershipAnomalyError("Durable upload lease key is corrupt");
	}
	if (!Number.isSafeInteger(record.epoch) || record.epoch <= 0) {
		throw new DurableOwnershipAnomalyError("Durable upload lease epoch is corrupt");
	}
	if (!Number.isSafeInteger(record.acquiredAt) || !Number.isSafeInteger(record.expiresAt) || record.expiresAt < record.acquiredAt) {
		throw new DurableOwnershipAnomalyError("Durable upload lease timestamps are corrupt");
	}
}

function assertTokenSafe(token: DurableLeaseToken): void {
	assertOwnerId(token.ownerId);
	assertOriginKey(token.originKey);
	assertIdentity(token.identity);
	if (!Number.isSafeInteger(token.epoch) || token.epoch <= 0) {
		throw new DurableOwnershipAnomalyError("Durable upload lease epoch is corrupt");
	}
	if (!Number.isSafeInteger(token.acquiredAt) || !Number.isSafeInteger(token.expiresAt) || token.expiresAt < token.acquiredAt) {
		throw new DurableOwnershipAnomalyError("Durable upload lease timestamps are corrupt");
	}
}

function assertClockSafe(record: DurableLeaseRecord, now: number): void {
	assertRecordSafe(record);
	if (now < record.acquiredAt) {
		throw new DurableOwnershipAnomalyError("Durable upload clock moved backwards or lease is future-dated");
	}
}

function toToken(record: DurableLeaseRecord, identity?: DurableItemIdentity): DurableLeaseToken {
	const recordIdentity = record.itemId ? { itemId: record.itemId, takeId: record.takeId } : undefined;
	return {
		kind: record.kind,
		originKey: record.originKey,
		identity: identity ?? recordIdentity,
		ownerId: record.ownerId,
		epoch: record.epoch,
		acquiredAt: record.acquiredAt,
		expiresAt: record.expiresAt,
		workGeneration: record.workGeneration
	};
}

/**
 * Does this token still name the acquisition the record holds?
 *
 * `expiresAt` is deliberately NOT compared (LSP-19044). It is the acquisition's mutable LIFETIME —
 * every `renewWriter`/`renewScheduler` rewrites it on the same record without changing who holds the
 * lease — so comparing it made a renewal silently invalidate every other live copy of its own token.
 * Admin's upload lease is one process-wide object whose `touch()` renews every session the page owns,
 * and both the per-chunk pool (CHUNK_CONCURRENCY = 3) and concurrent files in one batch call it at
 * the same time; the caller that renewed second held a snapshot taken before the first caller's
 * write, was told it no longer owned the lease, and dropped its token. `uploadPendingChunks` asks
 * `lease.owns()` BEFORE its first PUT, so every file in the batch reported UploadOwnershipLostError
 * and the driver returned the benign "yielded" outcome: no error, and not one byte sent to S3.
 *
 * Liveness is unaffected: every caller separately refuses a record whose own `expiresAt <= now`.
 * Release stays binding because every site that ends a lease stamps `releasedExpiry` — see that
 * helper for why stamping `now` was NOT sufficient once this comparison was removed.
 */
function recordMatchesToken(record: DurableLeaseRecord | undefined, token: DurableLeaseToken): boolean {
	if (!record || record.kind !== token.kind || record.originKey !== token.originKey) return false;
	if (record.ownerId !== token.ownerId || record.epoch !== token.epoch) return false;
	if (record.acquiredAt !== token.acquiredAt) return false;
	if (token.identity || record.itemId !== undefined || record.takeId !== undefined) {
		if (!token.identity || record.itemId !== token.identity.itemId || record.takeId !== token.identity.takeId) return false;
	}
	return true;
}

/**
 * LSP-23117: may the caller take over this UNEXPIRED lease? Only when it names exactly the owner and epoch
 * the row holds, and only for a row whose owner recorded a liveness lock. The caller proved that lock free
 * and holds it for the whole acquisition, so the named owner is gone and cannot come back mid-handover.
 */
function isReplaceableOwner(existing: DurableLeaseRecord, replaceOwner: DurableLeaseLiveness["replaceOwner"]): boolean {
	return (
		replaceOwner !== undefined &&
		existing.livenessLock !== undefined &&
		existing.ownerId === replaceOwner.ownerId &&
		existing.epoch === replaceOwner.epoch
	);
}

/** LSP-23117: the stamp to write now; called inside the transaction, right before the row is built. */
function livenessStampNow(source: { livenessLock?: string; readLivenessLock?: () => string | undefined }): string | undefined {
	return source.readLivenessLock ? source.readLivenessLock() : source.livenessLock;
}

/**
 * LSP-23117: `record` with its liveness stamp set to exactly `livenessLock`, removed when undefined. A same-owner
 * write from an owner that does not hold its lock right now must not leave an old stamp behind: a successor would
 * find that lock free and take over a live owner.
 */
function withLivenessStamp(record: DurableLeaseRecord, livenessLock: string | undefined): DurableLeaseRecord {
	const { livenessLock: previousLivenessLock, ...unstamped } = record;
	void previousLivenessLock;
	return livenessLock === undefined ? unstamped : { ...unstamped, livenessLock };
}

function createWorkRecord(
	originKey: string,
	previous: DurableWorkRecord | undefined,
	input: { pendingCount?: number; hasDueWork?: boolean }
): DurableWorkRecord {
	if (input.pendingCount !== undefined && (!Number.isSafeInteger(input.pendingCount) || input.pendingCount < 0)) {
		throw new DurableOwnershipAnomalyError("Durable upload pending count is invalid");
	}
	if (input.hasDueWork !== undefined && typeof input.hasDueWork !== "boolean") {
		throw new DurableOwnershipAnomalyError("Durable upload due-work flag is invalid");
	}
	const pendingCount = Math.max(0, Math.floor(input.pendingCount ?? (previous?.pendingCount ?? 0) + 1));
	return {
		originKey,
		workGeneration: (previous?.workGeneration ?? 0) + 1,
		pendingCount,
		hasDueWork: input.hasDueWork ?? pendingCount > 0
	};
}

function isIdleWork(work: DurableWorkRecord | undefined): boolean {
	return !work || (work.pendingCount === 0 && !work.hasDueWork);
}

class IndexedDbDurableUploadDatabase implements DurableUploadDatabase {
	private constructor(private readonly database: IDBDatabase) {}

	static async open(options: IndexedDbDurableOwnershipOptions): Promise<IndexedDbDurableUploadDatabase> {
		const factory = options.indexedDb ?? globalThis.indexedDB;
		if (!factory) throw new DurableOwnershipUnavailableError("IndexedDB is not available");
		const name = options.databaseName ?? DURABLE_UPLOAD_DATABASE_NAME;
		const version = options.databaseVersion ?? DURABLE_UPLOAD_DATABASE_VERSION;
		return new Promise((resolve, reject) => {
			const request = factory.open(name, version);
			request.onupgradeneeded = () => {
				const database = request.result;
				for (const storeName of Object.values(DURABLE_UPLOAD_STORE_NAMES)) {
					if (!database.objectStoreNames.contains(storeName)) database.createObjectStore(storeName);
				}
				for (const storeName of options.appOwnedStoreNames ?? []) {
					if (storeName && !database.objectStoreNames.contains(storeName)) database.createObjectStore(storeName);
				}
				for (const definition of options.appOwnedStoreDefinitions ?? []) {
					if (definition.name && !database.objectStoreNames.contains(definition.name)) {
						database.createObjectStore(definition.name, {
							...(definition.keyPath === undefined ? {} : { keyPath: definition.keyPath }),
							autoIncrement: definition.autoIncrement ?? false
						});
					}
				}
			};
			request.onerror = () => reject(request.error ?? new DurableOwnershipUnavailableError());
			request.onblocked = () => reject(new DurableOwnershipUnavailableError("IndexedDB upgrade is blocked"));
			request.onsuccess = () => resolve(new IndexedDbDurableUploadDatabase(request.result));
		});
	}

	transaction<T>(
		storeNames: readonly DurableUploadStoreName[],
		mode: "readonly" | "readwrite",
		callback: (transaction: DurableUploadTransaction) => Promise<T> | T
	): Promise<T> {
		const uniqueStoreNames = [...new Set(storeNames)];
		return new Promise((resolve, reject) => {
			let result: T;
			let callbackFinished = false;
			let settled = false;
			let transaction: IDBTransaction;
			try {
				transaction = this.database.transaction(uniqueStoreNames, mode);
			} catch (error) {
				reject(error);
				return;
			}
			const request = <Value>(operation: IDBRequest<Value>): Promise<Value> =>
				new Promise((requestResolve, requestReject) => {
					operation.onsuccess = () => requestResolve(operation.result);
					operation.onerror = () => requestReject(operation.error);
				});
			const transactionView: DurableUploadTransaction = {
				get: (storeName, key) => request(transaction.objectStore(storeName).get(key)),
				put: async (storeName, value, key) => {
					const putRequest =
						key === undefined
							? transaction.objectStore(storeName).put(value)
							: transaction.objectStore(storeName).put(value, key);
					await request(putRequest);
				},
				delete: async (storeName, key) => {
					await request(transaction.objectStore(storeName).delete(key));
				}
			};
			transaction.oncomplete = () => {
				if (!settled && callbackFinished) {
					settled = true;
					resolve(result);
				}
			};
			transaction.onerror = () => {
				if (!settled) {
					settled = true;
					reject(transaction.error);
				}
			};
			transaction.onabort = () => {
				if (!settled) {
					settled = true;
					reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
				}
			};
			Promise.resolve()
				.then(() => callback(transactionView))
				.then((value) => {
					result = value;
					callbackFinished = true;
				})
				.catch((error) => {
					if (!settled) {
						settled = true;
						try {
							transaction.abort();
						} catch {
							// The transaction may already have completed; preserve the original error.
						}
						reject(error);
					}
				});
		});
	}
}

export async function openIndexedDbDurableOwnership(options: IndexedDbDurableOwnershipOptions = {}): Promise<DurableOwnershipPort> {
	return createDurableOwnership(await IndexedDbDurableUploadDatabase.open(options));
}

export function createDurableOwnership(database: DurableUploadDatabase): DurableOwnershipPort {
	const coordinationStores = Object.values(DURABLE_UPLOAD_STORE_NAMES);
	const leaseStores = [DURABLE_UPLOAD_STORE_NAMES.leases];
	const workStores = [DURABLE_UPLOAD_STORE_NAMES.work];
	const poisonStores = [DURABLE_UPLOAD_STORE_NAMES.poison];
	const fencedTransactionContext = new WeakMap<object, { originKey: string; identity: DurableItemIdentity }>();

	const ownership: DurableOwnershipPort = {
		async acquireScheduler({ originKey, ownerId, identity, ttlMs, now: providedNow, livenessLock, readLivenessLock, replaceOwner }) {
			assertOwnerId(ownerId);
			assertOriginKey(originKey);
			assertIdentity(identity);
			const leaseKey = schedulerLeaseKey(originKey);
			return database.transaction([...coordinationStores], "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const work = await transaction.get<DurableWorkRecord>(DURABLE_UPLOAD_STORE_NAMES.work, originKey);
				if (!work || isIdleWork(work)) return undefined;
				const existing = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, leaseKey);
				if (existing) {
					assertClockSafe(existing, now);
					if (existing.expiresAt > now) {
						if (existing.ownerId !== ownerId || existing.itemId !== identity?.itemId || existing.takeId !== identity?.takeId) {
							if (!isReplaceableOwner(existing, replaceOwner)) return undefined;
						} else {
							const renewed = withLivenessStamp(
								{ ...existing, expiresAt: leaseExpiry(now, ttlMs) },
								livenessStampNow({ livenessLock, readLivenessLock })
							);
							await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, renewed, leaseKey);
							return toToken(renewed, identity);
						}
					}
				}
				const epoch = existing ? existing.epoch + 1 : 1;
				if (!Number.isSafeInteger(epoch)) throw new DurableOwnershipAnomalyError("Durable upload lease epoch overflowed");
				const schedulerStamp = livenessStampNow({ livenessLock, readLivenessLock });
				const record: DurableLeaseRecord = {
					key: leaseKey,
					kind: "scheduler",
					originKey,
					itemId: identity?.itemId,
					takeId: identity?.takeId,
					ownerId,
					epoch,
					acquiredAt: now,
					expiresAt: leaseExpiry(now, ttlMs),
					workGeneration: work.workGeneration,
					...(schedulerStamp === undefined ? {} : { livenessLock: schedulerStamp })
				};
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, record, leaseKey);
				return toToken(record, identity);
			});
		},

		async renewScheduler({ token, ttlMs, now: providedNow, liveness }) {
			if (token.kind !== "scheduler") return undefined;
			assertTokenSafe(token);
			return database.transaction(leaseStores, "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const key = schedulerLeaseKey(token.originKey);
				const current = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, key);
				if (!current) return undefined;
				assertClockSafe(current, now);
				// Renewal is deliberately not a reacquisition: an expired or replaced epoch
				// loses here and must let a successor drain safely.
				if (current.expiresAt <= now || !recordMatchesToken(current, token)) return undefined;
				const extended: DurableLeaseRecord = { ...current, expiresAt: leaseExpiry(now, ttlMs) };
				const renewed = liveness ? withLivenessStamp(extended, livenessStampNow(liveness)) : extended;
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, renewed, key);
				return toToken(renewed, token.identity);
			});
		},

		async releaseScheduler({ token, isIdle, now: providedNow }) {
			if (token.kind !== "scheduler") return { kind: "skipped-not-owned" };
			assertTokenSafe(token);
			if (!(await isIdle())) return { kind: "skipped-not-owned" };
			return database.transaction([...coordinationStores], "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const current = await transaction.get<DurableLeaseRecord>(
					DURABLE_UPLOAD_STORE_NAMES.leases,
					schedulerLeaseKey(token.originKey)
				);
				const work = await transaction.get<DurableWorkRecord>(DURABLE_UPLOAD_STORE_NAMES.work, token.originKey);
				if (current) assertRecordSafe(current);
				// The idle publication itself increments workGeneration. Requiring equality here would
				// make the only safe release path impossible: the scheduler must publish zero due work
				// before releasing, and that publication necessarily changes the generation. Reject an
				// older generation (a stale owner), but allow a newer generation only when the durable
				// work row is already idle. An enqueue racing this transaction leaves work non-idle and
				// therefore keeps the scheduler lease.
				if (
					!recordMatchesToken(current, token) ||
					!current ||
					current.expiresAt <= now ||
					!isIdleWork(work) ||
					(work?.workGeneration ?? 0) < (token.workGeneration ?? 0)
				) {
					return { kind: "skipped-not-owned" };
				}
				assertClockSafe(current, now);
				await transaction.put(
					DURABLE_UPLOAD_STORE_NAMES.leases,
					{ ...current, expiresAt: releasedExpiry(current) },
					schedulerLeaseKey(token.originKey)
				);
				return { kind: "applied", value: undefined };
			});
		},

		/**
		 * Shutdown/handover release — see `DurableOwnershipPort.abandonScheduler` for why this exists
		 * alongside the idle-gated `releaseScheduler` above.
		 *
		 * Fenced on OWNER IDENTITY and nothing else. It can only clear a lease whose `ownerId` is the
		 * one supplied, so it can never take a lease away from another tab, and it makes no liveness
		 * judgement about anybody. Deliberately NOT gated on idleness or `workGeneration`: the caller is
		 * stating this owner cannot continue, and with pending work the idle gate would refuse exactly
		 * when releasing matters most.
		 *
		 * Stamps `releasedExpiry` rather than deleting the row, the same way `releaseScheduler` does, so
		 * the epoch history a later acquire increments from is preserved.
		 */
		async abandonScheduler({ originKey, ownerId, now: providedNow }) {
			assertOwnerId(ownerId);
			assertOriginKey(originKey);
			const leaseKey = schedulerLeaseKey(originKey);
			return database.transaction([...coordinationStores], "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const current = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, leaseKey);
				if (!current) return { kind: "skipped-not-owned" };
				assertRecordSafe(current);
				if (current.ownerId !== ownerId) return { kind: "skipped-not-owned" };
				// An already-decayed lease needs no write, and rewriting it would only churn the row.
				if (current.expiresAt <= now) return { kind: "skipped-not-owned" };
				assertClockSafe(current, now);
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, { ...current, expiresAt: releasedExpiry(current) }, leaseKey);
				return { kind: "applied", value: undefined };
			});
		},

		readSchedulerLease: (originKey) => {
			assertOriginKey(originKey);
			return database.transaction(leaseStores, "readonly", (transaction) =>
				transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, schedulerLeaseKey(originKey))
			);
		},

		async noteWork({ originKey, pendingCount, hasDueWork }) {
			return database.transaction(workStores, "readwrite", async (transaction) => {
				const previous = await transaction.get<DurableWorkRecord>(DURABLE_UPLOAD_STORE_NAMES.work, originKey);
				const next = createWorkRecord(originKey, previous, {
					pendingCount,
					hasDueWork
				});
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.work, next, originKey);
				return next;
			});
		},

		readWork: (originKey) =>
			database.transaction(workStores, "readonly", (transaction) => transaction.get(DURABLE_UPLOAD_STORE_NAMES.work, originKey)),

		async acquireWriter({ originKey, identity, ownerId, ttlMs, now: providedNow, livenessLock, readLivenessLock, replaceOwner }) {
			assertOwnerId(ownerId);
			assertOriginKey(originKey);
			assertIdentity(identity);
			const leaseKey = writerLeaseKey(originKey, identity);
			return database.transaction(leaseStores, "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const existing = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, leaseKey);
				if (existing) {
					assertClockSafe(existing, now);
					if (existing.itemId !== identity.itemId || existing.takeId !== identity.takeId) {
						throw new DurableOwnershipAnomalyError("Durable upload item identity changed");
					}
					if (existing.expiresAt > now) {
						if (existing.ownerId === ownerId) {
							const sameOwnerStamp = livenessStampNow({ livenessLock, readLivenessLock });
							if (existing.livenessLock === sameOwnerStamp) return toToken(existing, identity);
							// The owner's lock state changed since it wrote this row: stamp a lock granted after the first
							// claim, or clear one it no longer holds (see `withLivenessStamp`). Same acquisition, so the
							// token is unchanged.
							const stamped = withLivenessStamp(existing, sameOwnerStamp);
							await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, stamped, leaseKey);
							return toToken(stamped, identity);
						}
						if (!isReplaceableOwner(existing, replaceOwner)) return undefined;
					}
				}
				const epoch = existing ? existing.epoch + 1 : 1;
				if (!Number.isSafeInteger(epoch)) throw new DurableOwnershipAnomalyError("Durable upload lease epoch overflowed");
				const writerStamp = livenessStampNow({ livenessLock, readLivenessLock });
				const record: DurableLeaseRecord = {
					key: leaseKey,
					kind: "writer",
					originKey,
					itemId: identity.itemId,
					takeId: identity.takeId,
					ownerId,
					epoch,
					acquiredAt: now,
					expiresAt: leaseExpiry(now, ttlMs),
					...(writerStamp === undefined ? {} : { livenessLock: writerStamp })
				};
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, record, leaseKey);
				return toToken(record, identity);
			});
		},

		readWriterLease: (originKey, identity) => {
			assertOriginKey(originKey);
			assertIdentity(identity);
			return database.transaction(leaseStores, "readonly", (transaction) =>
				transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, writerLeaseKey(originKey, identity))
			);
		},

		async renewWriter({ token, ttlMs, now: providedNow, liveness }) {
			if (token.kind !== "writer") return undefined;
			assertTokenSafe(token);
			return database.transaction(leaseStores, "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const current = await transaction.get<DurableLeaseRecord>(
					DURABLE_UPLOAD_STORE_NAMES.leases,
					writerLeaseKey(token.originKey, token.identity!)
				);
				if (!current) return undefined;
				assertClockSafe(current!, now);
				if (!recordMatchesToken(current, token)) return undefined;
				if (current!.expiresAt <= now) return undefined;
				const extended: DurableLeaseRecord = { ...current!, expiresAt: leaseExpiry(now, ttlMs) };
				const renewed = liveness ? withLivenessStamp(extended, livenessStampNow(liveness)) : extended;
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, renewed, renewed.key);
				return toToken(renewed, token.identity);
			});
		},

		async releaseWriter(token, providedNow) {
			const identity = token.identity;
			if (token.kind !== "writer" || !identity) return { kind: "skipped-not-owned" };
			assertTokenSafe(token);
			return database.transaction(leaseStores, "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const key = writerLeaseKey(token.originKey, identity);
				const current = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, key);
				if (current) assertRecordSafe(current);
				if (!current || current.expiresAt <= now || !recordMatchesToken(current, token)) return { kind: "skipped-not-owned" };
				assertClockSafe(current, now);
				await transaction.put(DURABLE_UPLOAD_STORE_NAMES.leases, { ...current, expiresAt: releasedExpiry(current) }, key);
				return { kind: "applied", value: undefined };
			});
		},

		async ownsWriter(token, providedNow) {
			if (token.kind !== "writer" || !token.identity) return false;
			assertTokenSafe(token);
			return database.transaction(leaseStores, "readonly", async (transaction) => {
				const now = validNow(providedNow);
				const current = await transaction.get<DurableLeaseRecord>(
					DURABLE_UPLOAD_STORE_NAMES.leases,
					writerLeaseKey(token.originKey, token.identity!)
				);
				if (!current) return false;
				assertClockSafe(current, now);
				return recordMatchesToken(current, token) && current.expiresAt > now;
			});
		},

		async commitIfWriterOwns({ token, storeNames = [], now: providedNow, effect }) {
			if (token.kind !== "writer" || !token.identity) return { kind: "skipped-not-owned" };
			assertTokenSafe(token);
			// Poison cleanup is part of a successful writer-fenced state transition. Keep
			// its store in every writer commit so the effect cannot fall back to a
			// check-then-delete in a second transaction.
			const names = [...new Set([...leaseStores, ...poisonStores, ...storeNames])];
			return database.transaction(names, "readwrite", async (transaction) => {
				const now = validNow(providedNow);
				const key = writerLeaseKey(token.originKey, token.identity!);
				const current = await transaction.get<DurableLeaseRecord>(DURABLE_UPLOAD_STORE_NAMES.leases, key);
				if (!current) return { kind: "skipped-not-owned" };
				assertClockSafe(current, now);
				if (current.expiresAt <= now || !recordMatchesToken(current, token)) return { kind: "skipped-not-owned" };
				fencedTransactionContext.set(transaction as object, {
					originKey: token.originKey,
					identity: token.identity!
				});
				try {
					return { kind: "applied", value: await effect(transaction) };
				} finally {
					fencedTransactionContext.delete(transaction as object);
				}
			});
		},

		async recordPoisonFailure({ originKey, identity, reason, ceiling, now: providedNow, transaction }) {
			if (!Number.isSafeInteger(ceiling) || ceiling < 1) throw new Error("Poison-item ceiling must be a positive integer");
			const write = async (activeTransaction: DurableUploadTransaction): Promise<DurablePoisonRecord> => {
				const now = validNow(providedNow);
				if (transaction) {
					const context = fencedTransactionContext.get(transaction as object);
					if (
						!context ||
						context.originKey !== originKey ||
						context.identity.itemId !== identity.itemId ||
						context.identity.takeId !== identity.takeId
					)
						throw new DurableOwnershipAnomalyError("Poison failure identity is not the current writer identity");
				}
				const key = poisonRecordKey(originKey, identity);
				const previous = await activeTransaction.get<DurablePoisonRecord>(DURABLE_UPLOAD_STORE_NAMES.poison, key);
				const record: DurablePoisonRecord = {
					key,
					itemId: identity.itemId,
					takeId: identity.takeId,
					failureCount: (previous?.failureCount ?? 0) + 1,
					ceiling,
					lastFailureAt: now,
					lastReason: reason
				};
				await activeTransaction.put(DURABLE_UPLOAD_STORE_NAMES.poison, record, key);
				return record;
			};
			if (transaction) return write(transaction);
			return database.transaction(poisonStores, "readwrite", write);
		},

		readPoisonFailure: (originKey, identity) =>
			database.transaction(poisonStores, "readonly", (transaction) =>
				transaction.get(DURABLE_UPLOAD_STORE_NAMES.poison, poisonRecordKey(originKey, identity))
			),

		async isPoisoned(originKey, identity) {
			const record = await ownership.readPoisonFailure(originKey, identity);
			return Boolean(record && record.failureCount >= record.ceiling);
		},

		async clearPoisonFailure(originKey, identity, transaction) {
			const key = poisonRecordKey(originKey, identity);
			if (transaction) {
				await transaction.delete(DURABLE_UPLOAD_STORE_NAMES.poison, key);
				return;
			}
			await database.transaction(poisonStores, "readwrite", (cleanupTransaction) =>
				cleanupTransaction.delete(DURABLE_UPLOAD_STORE_NAMES.poison, key)
			);
		}
	};

	return ownership;
}

export interface DurableWriterClaimOptions {
	ownership: DurableOwnershipPort;
	originKey: string;
	identity: DurableItemIdentity;
	ownerId: string;
	ttlMs: number;
	/** Stores the writer-fenced commit transaction must span. */
	storeNames: readonly DurableUploadStoreName[];
}

/**
 * The plain writer-lease claim: acquire, then expose owns/renew/commit/release over the
 * single token that acquisition returned, rotating the token in place on renewal.
 *
 * This is deliberately the *unadorned* shape. Lanes whose claim additionally re-resolves an
 * optional ownership port, keeps its token in a lane-level registry other methods read,
 * schedules an admission retry on acquisition failure, injects a clock, or gates currency on
 * a lane predicate build their claim by hand — those are different algorithms, not options.
 */
export async function createDurableWriterClaim(options: DurableWriterClaimOptions): Promise<SmallFileUploadClaim | undefined> {
	const { ownership, originKey, identity, ownerId, ttlMs, storeNames } = options;
	let token = await ownership.acquireWriter({
		originKey,
		identity,
		ownerId,
		ttlMs
	});
	if (!token) return undefined;
	return {
		isCurrent: () => ownership.ownsWriter(token!),
		renew: async () => {
			const renewed = await ownership.renewWriter({ token: token!, ttlMs });
			if (!renewed) return false;
			token = renewed;
			return true;
		},
		commitIfWriterOwns: (effect) => ownership.commitIfWriterOwns({ token: token!, storeNames, effect }),
		release: async () => {
			await ownership.releaseWriter(token!);
		}
	};
}
